import React, {useEffect, useState} from "react";
import {createPortal} from "react-dom";
import RecursiveParentSelect from "./RecursiveParentSelect";
import "./codeCreateModal.css";

function CodeCreateModal({
    open,
    onClose,
    title = "코드 만들기",
    value,
    rootParentId,
    categories,
    recursive,
    onCreate,
    onUpdate,
    onSuccess,
    mode = "create",
}) {
    const [name, setName] = useState("");
    const [parentId, setParentId] = useState(undefined);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setParentId(value);
        setName("");
        setLoading(false);
    }, [open, value]);

    const handleSubmit = async () => {
        if (loading || !name?.trim()) return;

        const payload = {
            parentId,
            rootParentId,
            name: name.trim(),
        };

        setLoading(true);
        try {
            const handler = mode === "update" ? onUpdate : onCreate;
            if (!handler) return;
            const res = await handler(payload);
            if (res?.status === 200) {
                await onSuccess?.();
                if (mode === "update") {
                    alert("수정되었습니다.");
                    onClose?.();
                } else if (window.confirm("등록되었습니다.\n계속 등록하시겠습니까?")) {
                    setName("");
                } else {
                    onClose?.();
                }
            }
        } catch (err) {
            console.error(err);
            alert("등록 중 오류가 발생했습니다.");
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleSubmit();
        }
    };

    if (!open || typeof document === "undefined") return null;

    return createPortal(
        <div className="code-create-modal-overlay" onClick={onClose}>
            <div className="code-create-modal" onClick={(e) => e.stopPropagation()}>
                <div className="code-create-modal-header">
                    <div className="code-create-modal-title">{title}</div>
                    <button type="button" className="code-create-modal-close" onClick={onClose} aria-label="닫기">
                        ×
                    </button>
                </div>

                <div className="code-create-modal-body">
                    {loading ? (
                        <p className="code-create-modal-loading">등록 중입니다...</p>
                    ) : (
                        <>
                            <p className="code-create-modal-hint">
                                필수 값은 별표로 표시되어 있습니다. <span className="text-red-500">*</span>
                            </p>

                            <label className="code-create-modal-label">
                                이름 <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                className="code-create-modal-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                onKeyDown={handleKeyDown}
                                autoFocus
                            />

                            <label className="code-create-modal-label mt-4">
                                범주 <span className="text-red-500">*</span>
                            </label>
                            <p className="code-create-modal-subhint">어디에 속한 코드인가요</p>

                            {recursive ? (
                                <RecursiveParentSelect
                                    data={categories}
                                    value={parentId != null ? {id: parentId} : undefined}
                                    onChange={(k) => setParentId(k?.id)}
                                    label="부모 선택"
                                />
                            ) : (
                                <RecursiveParentSelect
                                    data={categories}
                                    value={parentId != null ? {id: parentId} : undefined}
                                    onChange={(k) => setParentId(k?.id)}
                                    label="부모 선택"
                                />
                            )}

                            <div className="code-create-modal-footer">
                                <div className="code-create-modal-actions">
                                    <button type="button" className="code-create-modal-btn-cancel" onClick={onClose}>
                                        취소
                                    </button>
                                    <button
                                        type="button"
                                        className={`code-create-modal-btn-submit ${name.trim() ? "is-active" : ""}`}
                                        onClick={handleSubmit}
                                        disabled={!name.trim()}
                                    >
                                        {mode === "update" ? "수정하기" : "등록하기"}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}

export default CodeCreateModal;
